import { useEffect, useState, type FormEvent } from "react";
import { collection, deleteField, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { editStamp } from "../lib/editStamp";
import { lotKey, paths, type Job } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { useJobSettings } from "../hooks/useJobSettings";
import { useAuth } from "../hooks/useAuth";
import { CustomerPicker } from "./CustomerPicker";
import { JobTypeTitle, finalizeJobTitle } from "./JobTypeTitle";
import { ProjectItemSelect, jobItemCopy, useProjectItems } from "./ProjectItems";
import { resolveLot, useProjectLots } from "../lib/lots";
import { AddressField, locatedOf, type Located } from "./AddressField";

const pad = (n: number) => String(n).padStart(2, "0");
const localDate = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const localTime = (ms: number) => {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const textareaClass =
  "w-full rounded-lg border border-steel/40 bg-white px-3 py-2 text-sm text-ink placeholder:text-steel focus:border-brandorange focus:outline-none focus:ring-1 focus:ring-brandorange";

// Office edit of everything set when the job was booked: type, title,
// customer, description, when, where, PO and who's going.
export function JobEditCard({
  tenantId,
  jobId,
  job,
  onDone,
}: {
  tenantId: string;
  jobId: string;
  job: Job;
  onDone: () => void;
}) {
  const { types, pay: jobPay } = useJobSettings(tenantId);
  const projectItems = useProjectItems(tenantId, job.projectId);
  const projectLots = useProjectLots(tenantId, job.projectId);
  const [loc, setLoc] = useState<Located>(() => locatedOf(job));
  const { user } = useAuth();
  const [members, setMembers] = useState<Array<{ uid: string; name: string; role: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [f, setF] = useState({
    jobType: job.jobType ?? "",
    title: job.title,
    customerId: job.customerId ?? "",
    customerName: job.customerName ?? "",
    note: job.note,
    date: job.startAt ? localDate(job.startAt) : "",
    start: job.startAt ? localTime(job.startAt) : "08:00",
    end: job.endAt ? localTime(job.endAt) : "10:00",
    address: job.address,
    poNumber: job.poNumber ?? "",
    lot: job.lot ?? "",
    projectItemId: job.projectItem?.id ?? "",
    payMode: (job.payMode ?? "") as "" | "hourly" | "piece",
    noCustomerTexts: job.noCustomerTexts === true,
    assignedUids: job.assignedUids,
  });

  useEffect(
    () =>
      onSnapshot(collection(db, paths.tenantMembers(tenantId)), (snap) =>
        setMembers(
          snap.docs
            .filter((d) => d.get("disabled") !== true)
            .map((d) => ({
              uid: d.id,
              name: (d.get("displayName") as string) || (d.get("email") as string) || "Teammate",
              role: (d.get("role") as string) ?? "",
            }))
            .sort((a, b) => (a.role === "tech" ? 0 : 1) - (b.role === "tech" ? 0 : 1) || a.name.localeCompare(b.name)),
        ),
      ),
    [tenantId],
  );

  async function save(e: FormEvent) {
    e.preventDefault();
    const title = finalizeJobTitle(f.title, f.jobType);
    if (!title) {
      setError("Give the job a title or pick a type.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const startAt = f.date ? new Date(`${f.date}T${f.start}`).getTime() : null;
      let endAt = f.date ? new Date(`${f.date}T${f.end}`).getTime() : null;
      if (startAt !== null && endAt !== null && endAt <= startAt) endAt = startAt + 2 * 3600_000;
      const address = f.address.trim();
      // A project job's lot is a lot record: keep the link, or find / make the new lot.
      const item = projectItems.find((i) => i.id === f.projectItemId);
      const lotFields =
        job.projectId && f.lot.trim()
          ? job.lotId && lotKey(f.lot) === lotKey(job.lot ?? "")
            ? { lot: job.lot }
            : await resolveLot(tenantId, job.projectId, f.lot, projectLots, { address, projectItemId: item?.id ?? null, projectItemName: item?.name ?? "" }).then((l) =>
                l ? { lot: l.name, lotId: l.id } : { lot: f.lot.trim(), lotId: null },
              )
          : { lot: f.lot.trim() || deleteField(), lotId: null };
      await updateDoc(doc(db, paths.tenantJob(tenantId, jobId)), {
        title,
        jobType: f.jobType || deleteField(),
        customerId: f.customerId || null,
        customerName: f.customerId ? f.customerName : "",
        note: f.note.trim(),
        startAt,
        endAt,
        address,
        // A found or pinned spot is used as is; a typed address with no spot gets one from the server.
        ...(loc
          ? loc.lat !== job.lat || loc.lng !== job.lng
            ? { lat: loc.lat, lng: loc.lng }
            : {}
          : address !== job.address || job.lat != null
            ? { lat: null, lng: null, geocodeTriedAt: null }
            : {}),
        poNumber: f.poNumber.trim() || null,
        ...lotFields,
        payMode: f.payMode || jobPay[f.jobType]?.mode || "hourly",
        noCustomerTexts: f.noCustomerTexts,
        // Re-copy only when the pick changed, so a job keeps the prices it was booked at.
        ...(f.projectItemId !== (job.projectItem?.id ?? "")
          ? (() => {
              const it = projectItems.find((i) => i.id === f.projectItemId);
              return { projectItem: it ? jobItemCopy(it) : deleteField() };
            })()
          : {}),
        assignedUids: f.assignedUids,
        ...editStamp(user),
      });
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>Edit job</CardTitle>
      <form onSubmit={save} className="grid grid-cols-2 gap-3">
        <JobTypeTitle
          types={types}
          jobType={f.jobType}
          title={f.title}
          onChange={(next) => setF((p) => ({ ...p, ...next }))}
        />
        <div className="col-span-2">
          <Label>Customer</Label>
          <CustomerPicker
            tenantId={tenantId}
            value={f.customerId}
            valueName={f.customerName}
            onChange={(c) => setF((p) => ({ ...p, customerId: c?.id ?? "", customerName: c?.displayName ?? "" }))}
          />
        </div>
        <div className="col-span-2">
          <Label>Description</Label>
          <textarea
            className={textareaClass}
            rows={3}
            placeholder="What's going on? e.g. Customer called: the door won't close all the way."
            value={f.note}
            onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))}
          />
        </div>
        <div>
          <Label>Date</Label>
          <Input type="date" value={f.date} onChange={(e) => setF((p) => ({ ...p, date: e.target.value }))} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Start</Label>
            <Input type="time" value={f.start} onChange={(e) => setF((p) => ({ ...p, start: e.target.value }))} />
          </div>
          <div>
            <Label>End</Label>
            <Input type="time" value={f.end} onChange={(e) => setF((p) => ({ ...p, end: e.target.value }))} />
          </div>
        </div>
        <div className="col-span-2">
          <Label>Address</Label>
          <AddressField value={f.address} onChange={(address) => setF((p) => ({ ...p, address }))} located={loc} onLocate={setLoc} />
        </div>
        <div>
          <Label>Pay for this call</Label>
          <Select value={f.payMode || jobPay[f.jobType]?.mode || "hourly"} onChange={(e) => setF((p) => ({ ...p, payMode: e.target.value as "hourly" | "piece" }))}>
            <option value="hourly">Hourly (time clock)</option>
            <option value="piece">Piece rate</option>
          </Select>
        </div>
        <label className="col-span-2 flex items-center gap-2 text-xs text-ink">
          <input type="checkbox" checked={f.noCustomerTexts} onChange={(e) => setF((p) => ({ ...p, noCustomerTexts: e.target.checked }))} />
          Don't text the customer about this job (on the way, who's coming, review)
        </label>
        <div>
          <Label>PO number</Label>
          <Input value={f.poNumber} onChange={(e) => setF((p) => ({ ...p, poNumber: e.target.value }))} />
        </div>
        {job.projectId && projectItems.length > 0 ? (
          <div>
            <Label>Item (bid option)</Label>
            <ProjectItemSelect items={projectItems} value={f.projectItemId} onChange={(it) => setF((p) => ({ ...p, projectItemId: it?.id ?? "" }))} />
          </div>
        ) : null}
        <div>
          <Label>Lot</Label>
          <Input placeholder="e.g. Lot 26" list="edit-job-lots" value={f.lot} onChange={(e) => setF((p) => ({ ...p, lot: e.target.value }))} />
          <datalist id="edit-job-lots">
            {projectLots.map((l) => (
              <option key={l.id} value={l.name} />
            ))}
          </datalist>
        </div>
        <div className="col-span-2">
          <Label>Techs</Label>
          <div className="flex flex-wrap gap-1.5">
            {members.map((m) => {
              const on = f.assignedUids.includes(m.uid);
              return (
                <button
                  key={m.uid}
                  type="button"
                  aria-pressed={on}
                  className={cn(
                    "rounded-lg border px-2.5 py-1 text-xs",
                    on ? "border-ink bg-ink text-white" : "border-steel/30 bg-white text-ink hover:border-ink",
                  )}
                  onClick={() =>
                    setF((p) => ({
                      ...p,
                      assignedUids: on ? p.assignedUids.filter((u) => u !== m.uid) : [...p.assignedUids, m.uid],
                    }))
                  }
                >
                  {m.name}
                </button>
              );
            })}
            {members.length === 0 ? <span className="text-xs text-steel">No team members yet.</span> : null}
          </div>
        </div>
        <div className="col-span-2 flex items-center gap-2">
          <Button type="submit" busy={busy}>
            Save changes
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          {error ? <span className="text-xs text-danger">{error}</span> : null}
        </div>
      </form>
    </Card>
  );
}
